import { isComputationItem } from "./method-types.js";

// Pure dependency-graph construction for Computation items, kept apart from
// useFunctionRecompute so the graph logic is testable without React and the
// hook holds only lifecycle concerns.

/**
 * Extracts OID references from an expression string.
 *
 * Strategy: match every word-token (`\w+`) in the expression and intersect
 * with the set of known item OIDs in the form.  This is deliberately loose —
 * it won't miss a reference and false-positives only cause a harmless
 * extra-evaluation.  $THIS.FOO, $EVENT[key].FOO, and bare FOO are all covered
 * because the word-token scan picks up the identifier part regardless of
 * any prefix sigils.
 *
 * @param {string} expression
 * @param {Set<string>} knownOids
 * @returns {Set<string>}
 */
function extractReferencedOids(expression, knownOids) {
    const result = new Set();
    if (!expression) return result;
    const tokens = expression.match(/\w+/g) ?? [];
    for (const token of tokens) {
        if (knownOids.has(token)) {
            result.add(token);
        }
    }
    return result;
}

/**
 * Builds a topological ordering of `computedItems` (items with Computation
 * methodDef) using Kahn's algorithm.
 *
 * Items that are part of a cycle are omitted from the result and their OIDs
 * are returned separately so the caller can warn and skip them.
 *
 * @param {Array<{ oid: string, expression: string, dependsOn: Set<string> }>} computedItems
 * @returns {{ order: string[], cycleOids: string[] }}
 */
function buildTopoOrder(computedItems) {
    const computedOids = new Set(computedItems.map((c) => c.oid));

    // Build adjacency: edge A → B means "B depends on A"
    // i.e., A must be evaluated before B.
    const inDegree = new Map();
    const dependents = new Map(); // oid → set of oids that depend on it

    for (const { oid } of computedItems) {
        inDegree.set(oid, 0);
        dependents.set(oid, new Set());
    }

    for (const { oid, dependsOn } of computedItems) {
        for (const dep of dependsOn) {
            if (computedOids.has(dep)) {
                // dep must come before oid
                dependents.get(dep).add(oid);
                inDegree.set(oid, inDegree.get(oid) + 1);
            }
        }
    }

    // Kahn's BFS
    const queue = [];
    for (const [oid, deg] of inDegree) {
        if (deg === 0) queue.push(oid);
    }

    const order = [];
    while (queue.length > 0) {
        const oid = queue.shift();
        order.push(oid);
        for (const dependent of dependents.get(oid) ?? []) {
            const newDeg = inDegree.get(dependent) - 1;
            inDegree.set(dependent, newDeg);
            if (newDeg === 0) queue.push(dependent);
        }
    }

    // Any OID not in `order` is part of a cycle
    const cycleOids = computedItems
        .map((c) => c.oid)
        .filter((oid) => !order.includes(oid));

    return { order, cycleOids };
}

/**
 * Builds the Computation dependency graph for a form definition: the safe
 * (cycle-free) computed items, their topological evaluation order, and the
 * lookups the recompute passes need.
 *
 * @param {Array<{ oid: string, items: Array }>} itemGroups
 * @returns {{
 *   computedItems: Array<{ oid: string, expression: string, dependsOn: Set<string> }>,
 *   topoOrder: string[],
 *   cycleOids: string[],
 *   computedItemMap: Map<string, object>,
 *   computedOidsByGroup: Map<string, string[]>,
 * }}
 */
export function buildComputationGraph(itemGroups) {
    if (!itemGroups?.length) {
        return {
            computedItems: [],
            topoOrder: [],
            cycleOids: [],
            computedItemMap: new Map(),
            computedOidsByGroup: new Map(),
        };
    }

    // Collect all item OIDs so we can intersect during reference extraction
    const allOids = new Set();
    for (const ig of itemGroups) {
        for (const item of ig.items ?? []) {
            allOids.add(item.oid);
        }
    }

    // Collect Computation items with their expressions and item-group reference
    const items = [];
    const groupOidOfItem = new Map();
    for (const ig of itemGroups) {
        for (const item of ig.items ?? []) {
            const { methodDef, oid } = item;
            groupOidOfItem.set(oid, ig.oid);
            if (!isComputationItem(item)) {
                continue;
            }
            // Skip items where MethodDef is the default Imputation placeholder
            // (methodType=1, expression=null). This guard is defensive for the
            // Computation branch too: an item with no expression has nothing to compute.
            if (!methodDef.expression) {
                continue;
            }
            const dependsOn = extractReferencedOids(
                methodDef.expression,
                allOids
            );
            // A computed item's own OID may appear in its expression due to
            // the loose token scan — remove self-references to avoid false cycles.
            dependsOn.delete(oid);
            items.push({ oid, expression: methodDef.expression, dependsOn });
        }
    }

    const { order, cycleOids } = buildTopoOrder(items);

    if (cycleOids.length > 0) {
        console.warn(
            "[useFunctionRecompute] Cycle detected in Computation items — skipping recompute for:",
            cycleOids
        );
    }

    // Filter out cycle participants
    const cycleSet = new Set(cycleOids);
    const safeItems = items.filter((c) => !cycleSet.has(c.oid));

    // O(1) lookup Map: oid → item — replaces O(n) .find() calls in topo loops
    const computedItemMap = new Map(safeItems.map((c) => [c.oid, c]));

    // Group → its computed OIDs, so a group that stops being gated can have
    // exactly its own computed items recomputed.
    const computedOidsByGroup = new Map();
    for (const { oid } of safeItems) {
        const groupOid = groupOidOfItem.get(oid);
        if (!computedOidsByGroup.has(groupOid)) {
            computedOidsByGroup.set(groupOid, []);
        }
        computedOidsByGroup.get(groupOid).push(oid);
    }

    return {
        computedItems: safeItems,
        topoOrder: order,
        cycleOids,
        computedItemMap,
        computedOidsByGroup,
    };
}
